import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Autocomplete, TextField } from '@mui/material';
import {
  GET_ORGANIZATION_NUMBERS,
  GET_ALL_NUMBERS,
  GET_ALL_NUMBERS_INCLUDING_SMS_SENDER_ORGS,
} from 'src/graphql/organizations/queries';
import { useAuthContext } from 'src/auth/hooks';
import { ORG_PHONE_PURPOSE_CODES, TWILIO_SMS_SENDER_ORG_IDS } from 'src/constants/org-phones';
import { syncPhoneNumberMessagingServiceSIDs } from 'src/api/twilio';
import { useEffectiveRole } from 'src/hooks/use-effective-role';

// The roles /twilio/sync-number-service-sids admits — mirrors its roleCheck in
// backend/src/services/twilio/sync-number-service-sids.ts; change them together.
const SERVICE_SID_SYNC_ROLES = ['superadmin', 'manager', 'legislative_member'];

export const usePhoneNumberSelector = ({
  selectedOrganizationId,
  purposeCodes,
  autoSelectFirst = false,
  autoSelectForSelectedOrg = false,
  includeSmsSenderOrgNumbers = false,
}) => {
  const { user } = useAuthContext();
  const isSuperAdmin = user?.users_user_roles?.[0]?.user_roles_roles?.code === 'superadmin';

  // A volunteer — on the voter details page or the Assigned Tasks call console — would only
  // collect a 403 from the sync endpoint, so they skip it and the number's stored
  // messaging_service_id is used as is. Same per-org role the endpoint checks.
  const canSyncServiceSids = SERVICE_SID_SYNC_ROLES.includes(useEffectiveRole());
  const syncServiceSids = useCallback(
    (twilioIds) => {
      if (canSyncServiceSids) syncPhoneNumberMessagingServiceSIDs(twilioIds);
    },
    [canSyncServiceSids]
  );

  const effectivePurposeCodes = purposeCodes ?? [
    ORG_PHONE_PURPOSE_CODES.DIRECT_SND_SMS_PURPOSE_CODE,
  ];

  // In superadmin mode, optionally include every phone number belonging to the
  // configured Twilio SMS sender orgs (regardless of purpose code) on top of the
  // purpose-code filtered set.
  const includeSenderOrgs = isSuperAdmin && includeSmsSenderOrgNumbers;

  const superAdminQuery = includeSenderOrgs
    ? GET_ALL_NUMBERS_INCLUDING_SMS_SENDER_ORGS
    : GET_ALL_NUMBERS;

  const {
    data,
    loading: phoneNumberLoading,
    error: phoneNumberError,
  } = useQuery(isSuperAdmin ? superAdminQuery : GET_ORGANIZATION_NUMBERS, {
    variables: isSuperAdmin
      ? {
          purposeCode: { _in: effectivePurposeCodes },
          ...(includeSenderOrgs ? { smsSenderOrgIds: TWILIO_SMS_SENDER_ORG_IDS } : {}),
        }
      : {
          orgId: selectedOrganizationId,
          purposeCode: { _in: effectivePurposeCodes },
        },
    skip: !isSuperAdmin && !selectedOrganizationId,
  });
  const [orgPhoneNumber, setorgPhoneNumber] = useState();
  const [messagingServiceSid, setMessagingServiceSid] = useState();
  const [orgId, setorgId] = useState();
  const [selectedOrgPhoneNumberId, setSelectedOrgPhoneNumberId] = useState();

  let phoneNumbers = data?.ri_apps_prd_org_phone_numbers ?? [];
  // phoneNumbers = phoneNumbers.filter(
  //   (phoneNumber) =>
  //     phoneNumber?.purpose_code &&
  //     phoneNumber?.purpose_code ===
  //       SMS_PROVIDER_PURPOSE_CODE_MAP[phoneNumber?.org_phone_numbers_organization?.sms_provider]
  // );

  useEffect(() => {
    setorgPhoneNumber(null);
    setSelectedOrgPhoneNumberId(null);
  }, [selectedOrganizationId]);

  useEffect(() => {
    if (!autoSelectFirst || isSuperAdmin || selectedOrgPhoneNumberId || phoneNumbers.length === 0) {
      return;
    }
    const first = phoneNumbers[0];
    setorgId(first?.organization_id);
    setorgPhoneNumber(first?.phone_number);
    setMessagingServiceSid(first?.messaging_service_id);
    setSelectedOrgPhoneNumberId(first?.org_phone_number_id);
    if (first?.twilio_id) {
      syncServiceSids([first.twilio_id]);
    }
  }, [autoSelectFirst, isSuperAdmin, phoneNumbers, selectedOrgPhoneNumberId, syncServiceSids]);

  // Superadmin lists numbers across all orgs; when a caller launches from a
  // specific org context, preselect that org's first number instead of leaving
  // the selector on the alphabetically-first entry.
  useEffect(() => {
    if (
      !autoSelectForSelectedOrg ||
      !isSuperAdmin ||
      !selectedOrganizationId ||
      selectedOrgPhoneNumberId ||
      phoneNumbers.length === 0
    ) {
      return;
    }
    const match = phoneNumbers.find(
      (phoneNumber) => String(phoneNumber?.organization_id) === String(selectedOrganizationId)
    );
    if (!match) {
      return;
    }
    setorgId(match.organization_id);
    setorgPhoneNumber(match.phone_number);
    setMessagingServiceSid(match.messaging_service_id);
    setSelectedOrgPhoneNumberId(match.org_phone_number_id);
    if (match.twilio_id) {
      syncServiceSids([match.twilio_id]);
    }
  }, [
    autoSelectForSelectedOrg,
    isSuperAdmin,
    selectedOrganizationId,
    phoneNumbers,
    selectedOrgPhoneNumberId,
    syncServiceSids,
  ]);

  const setPhoneNumber = (str) => {
    const phoneNumber = data?.ri_apps_prd_org_phone_numbers?.find(
      (phoneNumber) => phoneNumber.phone_number === str
    );
    setorgId(phoneNumber?.organization_id);
    setorgPhoneNumber(phoneNumber?.phone_number);
    setMessagingServiceSid(phoneNumber?.messaging_service_id);
    setSelectedOrgPhoneNumberId(phoneNumber?.org_phone_number_id);
    syncServiceSids([phoneNumber.twilio_id]);
  };

  const getPhoneNumberLabel = (phoneNumber) =>
    isSuperAdmin
      ? `${phoneNumber.phone_number} (${phoneNumber.org_phone_numbers_organization?.name})`
      : phoneNumber.phone_number;

  const renderPhoneNumberSelector = ({ title, mb, filterPurposeCode }) => {
    let filteredOrgPhoneNumbers = phoneNumbers;
    if (filterPurposeCode) {
      filteredOrgPhoneNumbers = phoneNumbers.filter(
        (phoneNumber) => phoneNumber?.purpose_code === filterPurposeCode
      );
    }
    const label = title ?? 'Select Phone Number';
    return (
      <Autocomplete
        fullWidth
        disableClearable
        sx={{ mb: mb ? mb : 4 }}
        options={filteredOrgPhoneNumbers}
        value={
          phoneNumbers.find((pn) => pn.org_phone_number_id === selectedOrgPhoneNumberId) ?? null
        }
        isOptionEqualToValue={(option, value) =>
          option.org_phone_number_id === value.org_phone_number_id
        }
        getOptionLabel={getPhoneNumberLabel}
        renderOption={(props, phoneNumber) => (
          <li {...props} key={phoneNumber.org_phone_number_id}>
            {getPhoneNumberLabel(phoneNumber)}
          </li>
        )}
        onChange={(_, phoneNumber) => {
          setorgId(phoneNumber?.organization_id);
          setorgPhoneNumber(phoneNumber?.phone_number);
          setMessagingServiceSid(phoneNumber?.messaging_service_id);
          setSelectedOrgPhoneNumberId(phoneNumber?.org_phone_number_id);
          syncServiceSids([phoneNumber.twilio_id]);
        }}
        ListboxProps={{ style: { maxHeight: 400 } }}
        renderInput={(params) => <TextField {...params} label={label} placeholder={label} />}
      />
    );
  };

  // Callers render this as <RenderPhoneNumberSelector />, so it has to be the same component on
  // every render. A function declared fresh each time is a new component type to React, which
  // remounts the Autocomplete whenever the caller re-renders -- every keystroke in a sibling
  // message field -- and replays its label animation. The stable shell draws the latest version.
  const renderRef = useRef(renderPhoneNumberSelector);
  renderRef.current = renderPhoneNumberSelector;
  const [RenderPhoneNumberSelector] = useState(() => (props) => renderRef.current(props));

  const selectedPhoneNumberPurposeCode = phoneNumbers.find(
    (phoneNumber) => phoneNumber?.org_phone_number_id === selectedOrgPhoneNumberId
  )?.purpose_code;

  return {
    orgPhoneNumber,
    messagingServiceSid,
    orgId,
    phoneNumberLoading,
    phoneNumberError,
    RenderPhoneNumberSelector,
    setPhoneNumber,
    phoneNumbers,
    selectedPhoneNumberPurposeCode,
  };
};
